/**
 * 远程媒体状态机（Pure FSM）
 *
 * 核心职责：
 * 1. 定义状态（多维）
 * 2. 定义状态迁移规则
 * 3. 验证状态迁移合法性
 * 4. 不包含任何副作用
 *
 * 设计原则：
 * - 状态机是纯函数
 * - 副作用由外部监听状态变化执行
 * - 可单独测试、可视化
 */

// ========== 状态定义（多维） ==========

/**
 * 生命周期状态
 */
export const LifecycleState = {
  IDLE: 'idle',           // 初始状态
  JOINED: 'joined',       // 已加入频道
  LEFT: 'left',           // 已离开
}

/**
 * 媒体状态（视频）
 */
export const VideoState = {
  IDLE: 'idle',           // 无视频
  PUBLISHED: 'published', // 已发布但未播放
  PLAYING: 'playing',     // 正在播放
  FROZEN: 'frozen',       // 卡顿/冻结
  ENDED: 'ended',         // Track 已结束
}

/**
 * 媒体状态（音频）
 */
export const AudioState = {
  IDLE: 'idle',           // 无音频
  PUBLISHED: 'published', // 已发布
  PLAYING: 'playing',     // 正在播放
  MUTED: 'muted',         // 静音
  ENDED: 'ended',         // Track 已结束
}

/**
 * 视图状态
 */
export const ViewState = {
  UNMOUNTED: 'unmounted', // 未挂载
  MOUNTED: 'mounted',     // 已挂载 DOM
}

// ========== 事件定义 ==========

export const FSMEvent = {
  // 生命周期事件
  USER_JOINED: 'USER_JOINED',
  USER_LEFT: 'USER_LEFT',

  // 视频事件
  VIDEO_PUBLISHED: 'VIDEO_PUBLISHED',
  VIDEO_UNPUBLISHED: 'VIDEO_UNPUBLISHED',
  VIDEO_FIRST_FRAME: 'VIDEO_FIRST_FRAME',
  VIDEO_FROZEN: 'VIDEO_FROZEN',
  VIDEO_TRACK_ENDED: 'VIDEO_TRACK_ENDED',

  // 音频事件
  AUDIO_PUBLISHED: 'AUDIO_PUBLISHED',
  AUDIO_UNPUBLISHED: 'AUDIO_UNPUBLISHED',
  AUDIO_TRACK_ENDED: 'AUDIO_TRACK_ENDED',
  AUDIO_MUTED: 'AUDIO_MUTED',
  AUDIO_UNMUTED: 'AUDIO_UNMUTED',

  // 视图事件
  VIEW_MOUNTED: 'VIEW_MOUNTED',
  VIEW_UNMOUNTED: 'VIEW_UNMOUNTED',
}

// ========== 状态机核心 ==========

/**
 * 创建初始状态
 * @param {number} uid - 用户 ID
 * @returns {Object} 初始状态
 */
export function createInitialState(uid) {
  return {
    uid,
    lifecycle: LifecycleState.IDLE,
    video: {
      state: VideoState.IDLE,
      track: null,
      publishTime: null,
      firstFrameTime: null,
    },
    audio: {
      state: AudioState.IDLE,
      track: null,
    },
    view: {
      state: ViewState.UNMOUNTED,
      element: null,
    },
    lastUpdated: Date.now(),
  }
}

/**
 * 状态迁移规则表
 *
 * 格式：[当前状态, 事件] => 下一个状态
 *
 * 如果没有匹配的规则，迁移失败（保持原状态）
 */
const VIDEO_TRANSITIONS = {
  // IDLE 状态
  [`${VideoState.IDLE}-${FSMEvent.VIDEO_PUBLISHED}`]: VideoState.PUBLISHED,

  // PUBLISHED 状态
  [`${VideoState.PUBLISHED}-${FSMEvent.VIDEO_FIRST_FRAME}`]: VideoState.PLAYING,
  [`${VideoState.PUBLISHED}-${FSMEvent.VIDEO_UNPUBLISHED}`]: VideoState.IDLE,
  [`${VideoState.PUBLISHED}-${FSMEvent.VIDEO_TRACK_ENDED}`]: VideoState.ENDED,

  // PLAYING 状态
  [`${VideoState.PLAYING}-${FSMEvent.VIDEO_FROZEN}`]: VideoState.FROZEN,
  [`${VideoState.PLAYING}-${FSMEvent.VIDEO_UNPUBLISHED}`]: VideoState.IDLE,
  [`${VideoState.PLAYING}-${FSMEvent.VIDEO_TRACK_ENDED}`]: VideoState.ENDED,

  // FROZEN 状态
  [`${VideoState.FROZEN}-${FSMEvent.VIDEO_FIRST_FRAME}`]: VideoState.PLAYING,
  [`${VideoState.FROZEN}-${FSMEvent.VIDEO_UNPUBLISHED}`]: VideoState.IDLE,
  [`${VideoState.FROZEN}-${FSMEvent.VIDEO_TRACK_ENDED}`]: VideoState.ENDED,

  // ENDED 状态
  [`${VideoState.ENDED}-${FSMEvent.VIDEO_PUBLISHED}`]: VideoState.PUBLISHED,
}

const AUDIO_TRANSITIONS = {
  // IDLE 状态
  [`${AudioState.IDLE}-${FSMEvent.AUDIO_PUBLISHED}`]: AudioState.PLAYING,

  // PLAYING 状态
  [`${AudioState.PLAYING}-${FSMEvent.AUDIO_MUTED}`]: AudioState.MUTED,
  [`${AudioState.PLAYING}-${FSMEvent.AUDIO_UNPUBLISHED}`]: AudioState.IDLE,
  [`${AudioState.PLAYING}-${FSMEvent.AUDIO_TRACK_ENDED}`]: AudioState.ENDED,

  // MUTED 状态
  [`${AudioState.MUTED}-${FSMEvent.AUDIO_UNMUTED}`]: AudioState.PLAYING,
  [`${AudioState.MUTED}-${FSMEvent.AUDIO_UNPUBLISHED}`]: AudioState.IDLE,
  [`${AudioState.MUTED}-${FSMEvent.AUDIO_TRACK_ENDED}`]: AudioState.ENDED,

  // ENDED 状态
  [`${AudioState.ENDED}-${FSMEvent.AUDIO_PUBLISHED}`]: AudioState.PLAYING,
}

const VIEW_TRANSITIONS = {
  [`${ViewState.UNMOUNTED}-${FSMEvent.VIEW_MOUNTED}`]: ViewState.MOUNTED,
  [`${ViewState.MOUNTED}-${FSMEvent.VIEW_UNMOUNTED}`]: ViewState.UNMOUNTED,
}

const LIFECYCLE_TRANSITIONS = {
  [`${LifecycleState.IDLE}-${FSMEvent.USER_JOINED}`]: LifecycleState.JOINED,
  [`${LifecycleState.JOINED}-${FSMEvent.USER_LEFT}`]: LifecycleState.LEFT,
}

/**
 * 远程媒体状态机类
 */
export class RemoteMediaFSM {
  #state = null
  #listeners = []

  constructor(uid) {
    this.#state = createInitialState(uid)
  }

  /**
   * 获取当前状态（只读）
   */
  getState() {
    return { ...this.#state }
  }

  /**
   * 分发事件，触发状态迁移
   * @param {Object} event - 事件对象
   * @param {string} event.type - 事件类型
   * @param {Object} event.payload - 事件数据
   * @returns {Object} 迁移结果 { success, prevState, nextState, transitions }
   */
  dispatch(event) {
    const { type, payload = {} } = event
    const prevState = { ...this.#state }
    const transitions = []

    // 根据事件类型执行对应的迁移
    let nextState = { ...prevState }

    // 1. 生命周期迁移
    if (this.#isLifecycleEvent(type)) {
      const newLifecycle = this.#transition(
        LIFECYCLE_TRANSITIONS,
        prevState.lifecycle,
        type
      )
      if (newLifecycle !== prevState.lifecycle) {
        nextState.lifecycle = newLifecycle
        transitions.push({ dimension: 'lifecycle', from: prevState.lifecycle, to: newLifecycle })
      }
    }

    // 2. 视频迁移
    if (this.#isVideoEvent(type)) {
      const newVideoState = this.#transition(
        VIDEO_TRANSITIONS,
        prevState.video.state,
        type
      )
      if (newVideoState !== prevState.video.state) {
        nextState.video = {
          ...nextState.video,
          state: newVideoState,
        }
        transitions.push({ dimension: 'video', from: prevState.video.state, to: newVideoState })
      }

      // 更新视频相关数据
      if (type === FSMEvent.VIDEO_PUBLISHED && payload.track) {
        nextState.video.track = payload.track
        nextState.video.publishTime = Date.now()
      }
      if (type === FSMEvent.VIDEO_FIRST_FRAME && payload.firstFrameTime) {
        nextState.video.firstFrameTime = payload.firstFrameTime
      }
      if (type === FSMEvent.VIDEO_UNPUBLISHED || type === FSMEvent.VIDEO_TRACK_ENDED) {
        nextState.video.track = null
      }
    }

    // 3. 音频迁移
    if (this.#isAudioEvent(type)) {
      const newAudioState = this.#transition(
        AUDIO_TRANSITIONS,
        prevState.audio.state,
        type
      )
      if (newAudioState !== prevState.audio.state) {
        nextState.audio = {
          ...nextState.audio,
          state: newAudioState,
        }
        transitions.push({ dimension: 'audio', from: prevState.audio.state, to: newAudioState })
      }

      // 更新音频相关数据
      if (type === FSMEvent.AUDIO_PUBLISHED && payload.track) {
        nextState.audio.track = payload.track
      }
      if (type === FSMEvent.AUDIO_UNPUBLISHED || type === FSMEvent.AUDIO_TRACK_ENDED) {
        nextState.audio.track = null
      }
    }

    // 4. 视图迁移
    if (this.#isViewEvent(type)) {
      const newViewState = this.#transition(
        VIEW_TRANSITIONS,
        prevState.view.state,
        type
      )
      if (newViewState !== prevState.view.state) {
        nextState.view = {
          ...nextState.view,
          state: newViewState,
        }
        transitions.push({ dimension: 'view', from: prevState.view.state, to: newViewState })
      }

      // 更新视图相关数据
      if (type === FSMEvent.VIEW_MOUNTED && payload.element) {
        nextState.view.element = payload.element
      }
      if (type === FSMEvent.VIEW_UNMOUNTED) {
        nextState.view.element = null
      }
    }

    // 更新时间戳
    nextState.lastUpdated = Date.now()

    // 保存新状态
    this.#state = nextState

    const result = {
      success: transitions.length > 0,
      prevState,
      nextState,
      transitions,
      event,
    }

    // 通知监听器
    if (transitions.length > 0) {
      this.#notifyListeners(result)
    }

    return result
  }

  /**
   * 状态迁移函数
   * @param {Object} transitionTable - 迁移表
   * @param {string} currentState - 当前状态
   * @param {string} eventType - 事件类型
   * @returns {string} 下一个状态（如果无匹配规则，返回当前状态）
   */
  #transition(transitionTable, currentState, eventType) {
    const key = `${currentState}-${eventType}`
    const nextState = transitionTable[key]

    if (!nextState) {
      // 无匹配规则，保持当前状态（不是错误，只是忽略该事件）
      return currentState
    }

    return nextState
  }

  /**
   * 判断是否为生命周期事件
   */
  #isLifecycleEvent(type) {
    return [FSMEvent.USER_JOINED, FSMEvent.USER_LEFT].includes(type)
  }

  /**
   * 判断是否为视频事件
   */
  #isVideoEvent(type) {
    return [
      FSMEvent.VIDEO_PUBLISHED,
      FSMEvent.VIDEO_UNPUBLISHED,
      FSMEvent.VIDEO_FIRST_FRAME,
      FSMEvent.VIDEO_FROZEN,
      FSMEvent.VIDEO_TRACK_ENDED,
    ].includes(type)
  }

  /**
   * 判断是否为音频事件
   */
  #isAudioEvent(type) {
    return [
      FSMEvent.AUDIO_PUBLISHED,
      FSMEvent.AUDIO_UNPUBLISHED,
      FSMEvent.AUDIO_TRACK_ENDED,
      FSMEvent.AUDIO_MUTED,
      FSMEvent.AUDIO_UNMUTED,
    ].includes(type)
  }

  /**
   * 判断是否为视图事件
   */
  #isViewEvent(type) {
    return [
      FSMEvent.VIEW_MOUNTED,
      FSMEvent.VIEW_UNMOUNTED,
    ].includes(type)
  }

  /**
   * 订阅状态变化
   * @param {Function} listener - 监听器函数
   * @returns {Function} 取消订阅函数
   */
  subscribe(listener) {
    this.#listeners.push(listener)
    return () => {
      const index = this.#listeners.indexOf(listener)
      if (index > -1) {
        this.#listeners.splice(index, 1)
      }
    }
  }

  /**
   * 通知所有监听器
   */
  #notifyListeners(result) {
    this.#listeners.forEach(listener => {
      try {
        listener(result)
      } catch (error) {
        console.error('[FSM] Listener error:', error)
      }
    })
  }

  /**
   * 检查是否应该播放视频
   * （条件：视频已发布 + 视图已挂载 + 视频不在播放中）
   * @returns {boolean}
   */
  shouldPlayVideo() {
    const { video, view } = this.#state
    return (
      video.state === VideoState.PUBLISHED &&
      view.state === ViewState.MOUNTED &&
      view.element !== null &&
      video.track !== null
    )
  }

  /**
   * 销毁状态机
   */
  destroy() {
    this.#listeners = []
    this.#state = null
  }
}
